import { useMemo, useState, type FormEvent } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { supabase, ROLE_LABEL, type AppRole } from '@/lib/supabase'
import { useAuth } from '@/auth/useAuth'
import { Badge, Button, Card, ErrorNote, PageHeader } from '@/components/ui'

// The two roles the admin_allowlist_role check constraint accepts.
const KTM_ROLES = ['ktm_admin', 'ktm_staff'] as const
type KtmRole = (typeof KTM_ROLES)[number]

interface AllowlistRow {
  email: string
  role: KtmRole
  added_at: string
}

interface ProfileRow {
  id: string
  email: string
  full_name: string | null
  role: AppRole
  is_active: boolean
  last_seen_at: string | null
}

interface StaffRow {
  email: string
  role: KtmRole
  addedAt: string
  profile: ProfileRow | null
}

/**
 * CRUD over admin_allowlist plus the matching profiles row.
 *
 * The allowlist is the source of truth for "who can be a Ki Tua o Matariki
 * user". Their profile only exists after they've signed in for the first
 * time -- until then the row here reads "Not yet signed in".
 *
 * Role changes and revokes update BOTH the allowlist AND the profile (if
 * one exists), so the change takes effect the next request without
 * waiting for a fresh sign-in.
 */
export function KtmStaffPage() {
  const { profile: me } = useAuth()
  const queryClient = useQueryClient()
  const [error, setError] = useState<string | null>(null)

  const { data, isLoading } = useQuery({
    queryKey: ['admin', 'ktm-staff'],
    queryFn: async (): Promise<StaffRow[]> => {
      const [allowlist, profiles] = await Promise.all([
        supabase
          .from('admin_allowlist')
          .select('email, role, added_at')
          .order('email')
          .returns<AllowlistRow[]>(),
        supabase
          .from('profiles')
          .select('id, email, full_name, role, is_active, last_seen_at')
          .in('role', ['ktm_admin', 'ktm_staff'])
          .returns<ProfileRow[]>(),
      ])
      if (allowlist.error) throw allowlist.error
      if (profiles.error) throw profiles.error

      const byEmail = new Map((profiles.data ?? []).map((p) => [p.email.toLowerCase(), p]))
      return (allowlist.data ?? []).map((row) => ({
        email: row.email,
        role: row.role,
        addedAt: row.added_at,
        profile: byEmail.get(row.email.toLowerCase()) ?? null,
      }))
    },
  })

  const refresh = () =>
    queryClient.invalidateQueries({ queryKey: ['admin', 'ktm-staff'] })

  const add = useMutation({
    mutationFn: async (values: { email: string; role: KtmRole }) => {
      const email = values.email.trim().toLowerCase()
      if (!email) throw new Error('Give an email address.')
      const { error } = await supabase
        .from('admin_allowlist')
        .insert({ email, role: values.role })
      if (error) {
        // A duplicate email is the common case; name it plainly.
        if (error.code === '23505') {
          throw new Error(`${email} is already on the allowlist.`)
        }
        throw error
      }
      await refresh()
    },
    onError: (e: Error) => setError(e.message),
    onSuccess: () => setError(null),
  })

  const changeRole = useMutation({
    mutationFn: async (values: { email: string; role: KtmRole }) => {
      const email = values.email.toLowerCase()
      // Update the allowlist first (source of truth). Then propagate to the
      // profile if one already exists so the change is felt immediately.
      const allowlist = await supabase
        .from('admin_allowlist')
        .update({ role: values.role })
        .eq('email', email)
      if (allowlist.error) throw allowlist.error

      const profile = await supabase
        .from('profiles')
        .update({ role: values.role })
        .eq('email', email)
      if (profile.error) throw profile.error
      await refresh()
    },
    onError: (e: Error) => setError(e.message),
    onSuccess: () => setError(null),
  })

  const revoke = useMutation({
    mutationFn: async (email: string) => {
      const target = email.toLowerCase()
      if (!window.confirm(
        `Revoke Ki Tua o Matariki access for ${target}? They keep their sign-in but lose the admin/staff role.`,
      )) return
      // Drop the allowlist row so a future sign-in defaults to inactive
      // facilitator, and demote any existing profile now so the change is
      // immediate.
      const allowlist = await supabase.from('admin_allowlist').delete().eq('email', target)
      if (allowlist.error) throw allowlist.error

      const profile = await supabase
        .from('profiles')
        .update({ is_active: false, role: 'facilitator', org_id: null })
        .eq('email', target)
      if (profile.error) throw profile.error
      await refresh()
    },
    onError: (e: Error) => setError(e.message),
    onSuccess: () => setError(null),
  })

  const rows = useMemo(() => data ?? [], [data])

  return (
    <>
      <PageHeader title="Ki Tua o Matariki staff">
        Grant Ki Tua o Matariki access to a work email address, and choose whether they arrive as an
        admin (can change roles, tiers and resources) or as staff (aggregate view only). Removing
        someone drops their access on the next request; their sign-in itself is not deleted.
      </PageHeader>

      <Card className="mb-6 max-w-3xl">
        <AddForm onSubmit={(v) => add.mutate(v)} disabled={add.isPending} />
      </Card>

      {error && (
        <div className="mb-4">
          <ErrorNote>{error}</ErrorNote>
        </div>
      )}

      {isLoading && <p className="text-sm text-inksoft">Loading…</p>}

      <div className="overflow-x-auto rounded-md border border-line bg-surface">
        <table className="w-full text-sm">
          <thead className="border-b border-line text-left text-inksoft">
            <tr>
              <th className="px-4 py-2 font-medium">Email</th>
              <th className="px-4 py-2 font-medium">Name</th>
              <th className="px-4 py-2 font-medium">Role</th>
              <th className="px-4 py-2 font-medium">Status</th>
              <th className="px-4 py-2 font-medium text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => {
              const isSelf = me?.email.toLowerCase() === row.email.toLowerCase()
              const signedIn = row.profile !== null
              const deactivated = row.profile ? !row.profile.is_active : false
              return (
                <tr key={row.email} className="border-b border-line last:border-0">
                  <td className="px-4 py-2">
                    {row.email}
                    {isSelf && <span className="ml-2 text-xs text-inksoft">(you)</span>}
                  </td>
                  <td className="px-4 py-2 text-inksoft">
                    {row.profile?.full_name ?? <span className="text-inksoft/60">—</span>}
                  </td>
                  <td className="px-4 py-2">
                    <select
                      value={row.role}
                      onChange={(e) =>
                        changeRole.mutate({ email: row.email, role: e.target.value as KtmRole })
                      }
                      disabled={isSelf || changeRole.isPending}
                      className="rounded-sm border border-line bg-surface px-2 py-1"
                      aria-label={`Role for ${row.email}`}
                    >
                      {KTM_ROLES.map((r) => (
                        <option key={r} value={r}>{ROLE_LABEL[r]}</option>
                      ))}
                    </select>
                  </td>
                  <td className="px-4 py-2">
                    {!signedIn ? (
                      <Badge tone="locked">Not yet signed in</Badge>
                    ) : deactivated ? (
                      <Badge tone="alert">Deactivated</Badge>
                    ) : (
                      <Badge tone="good">Active</Badge>
                    )}
                  </td>
                  <td className="px-4 py-2 text-right">
                    <button
                      type="button"
                      onClick={() => revoke.mutate(row.email)}
                      disabled={isSelf || revoke.isPending}
                      className="text-sm text-alert underline underline-offset-4 hover:no-underline disabled:cursor-not-allowed disabled:opacity-40"
                      title={isSelf ? 'You cannot revoke your own access here.' : undefined}
                    >
                      Revoke
                    </button>
                  </td>
                </tr>
              )
            })}
            {rows.length === 0 && !isLoading && (
              <tr>
                <td colSpan={5} className="px-4 py-8 text-center text-sm text-inksoft">
                  No Ki Tua o Matariki staff on the allowlist yet.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </>
  )
}

function AddForm({
  onSubmit,
  disabled,
}: {
  onSubmit: (values: { email: string; role: KtmRole }) => void
  disabled: boolean
}) {
  const [email, setEmail] = useState('')
  const [role, setRole] = useState<KtmRole>('ktm_staff')

  function submit(event: FormEvent) {
    event.preventDefault()
    onSubmit({ email, role })
    setEmail('')
    setRole('ktm_staff')
  }

  return (
    <form onSubmit={submit} className="grid gap-3 sm:grid-cols-[1fr_auto_auto]">
      <label className="block text-sm">
        <span className="font-medium text-ink">Email address</span>
        <input
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          required
          placeholder="firstname.lastname@kituaomatariki.org.nz"
          className="mt-1 w-full rounded-sm border border-line px-3 py-2"
        />
      </label>
      <label className="block text-sm">
        <span className="font-medium text-ink">Role</span>
        <select
          value={role}
          onChange={(e) => setRole(e.target.value as KtmRole)}
          className="mt-1 w-full rounded-sm border border-line px-3 py-2"
        >
          {KTM_ROLES.map((r) => (
            <option key={r} value={r}>{ROLE_LABEL[r]}</option>
          ))}
        </select>
      </label>
      <div className="flex items-end">
        <Button type="submit" disabled={disabled}>
          {disabled ? 'Adding…' : 'Add'}
        </Button>
      </div>
    </form>
  )
}
